import { Capacitor } from '@capacitor/core';
import store from './store/store';

// Debugging flag
const DEBUG = false;

// Function to initialize OneSignal based on platform
export const initializeOneSignal = () => {
  try {
    if (Capacitor.isNative) {
      if (DEBUG) console.log('Initializing OneSignal for Mobile');
      initializeOneSignalMobile();
    } else {
      if (DEBUG) console.log('Initializing OneSignal for Web');
      initializeOneSignalWeb();
    }
  } catch (error) {
    console.error('Error initializing OneSignal:', error);
  }
};

// Web platform OneSignal initialization
const initializeOneSignalWeb = async () => {
  if (!window.OneSignal) {
    try {
      await loadOneSignalScript();
      await initializeOneSignalSDK();
      await handleNotificationPermission();
      await fetchTokenWithRetry();
    } catch (error) {
      console.error('OneSignal web initialization failed:', error);
    }
  } else {
    if (DEBUG) console.log('OneSignal is already loaded for Web platform');
    await fetchTokenWithRetry();
  }
};

const loadOneSignalScript = () => {
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://cdn.onesignal.com/sdks/OneSignalSDK.js';
    script.onload = resolve;
    script.onerror = () => reject(new Error('Failed to load OneSignal SDK'));
    document.head.appendChild(script);
  });
};

const initializeOneSignalSDK = () => {
  return new Promise((resolve) => {
    window.OneSignal = window.OneSignal || [];
    window.OneSignal.push(() => {
      window.OneSignal.init({
        appId: "46b06476-d1c3-4115-8b7e-a105cc5c480a",
        safari_web_id: "web.onesignal.auto.6a76584b-4903-4cb9-b550-82d6a06974fc",
      });

      // Add debugging event listeners
      if (DEBUG) {
        window.OneSignal.on('notificationDisplay', (event) => {
          console.log('OneSignal Notification displayed:', event);
        });
        window.OneSignal.on('notificationDismiss', (event) => {
          console.log('OneSignal Notification dismissed:', event);
        });
      }

      resolve();
    });
  });
};

const handleNotificationPermission = async () => {
  if (Notification.permission === 'default') {
    try {
      await window.OneSignal.showSlidedownPrompt();
    } catch (error) {
      console.warn('Failed to show permission prompt:', error);
    }
  } else if (Notification.permission === 'denied') {
    console.warn('Notifications are blocked by the user');
  }
};

// Improved token fetching with retry logic
const fetchTokenWithRetry = async (maxRetries = 5, delay = 3000) => {
  for (let retry = 0; retry < maxRetries; retry++) {
    try {
      if (Capacitor.isNative) {
        return await fetchTokenForMobile();
      } else {
        if (Notification.permission !== 'granted') {
          console.warn('Push notifications not granted');
          return;
        }

        const token = await window.OneSignal.getUserId();
        if (!token) throw new Error('Empty token received');

        if (DEBUG) console.log('OneSignal User ID:', token);
        
        store.dispatch('pushId', token);
        store.dispatch('syncUserIdWithServer');

        /*const userId = store.state.getUser.user_id;
        if (userId) {
          if (DEBUG) console.log('Setting userId tag:', userId);
          await window.OneSignal.sendTags({ userId });
        }*/

        return token;
      }
    } catch (error) {
      console.error(`Attempt ${retry + 1}/${maxRetries} failed:`, error);
      if (retry < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }
  throw new Error('Max retries reached for fetching OneSignal token');
};

// Fetch token for mobile devices
const fetchTokenForMobile = async () => {
  try {
    const { OneSignal } = window.Capacitor.Plugins;

    const hasPermission = await OneSignal.promptForPushNotificationsWithUserResponse();

    if (hasPermission) {
      const ids = await OneSignal.getIds();

      if (ids.userId) {
        if (DEBUG) console.log('Mobile OneSignal User ID:', ids.userId);
        
        store.dispatch('pushId', ids.userId);
        store.dispatch('syncUserIdWithServer');

        /*const userId = store.state.getUser.user_id;
        if (userId) {
          if (DEBUG) console.log('Setting mobile userId tag:', userId);
          await OneSignal.sendTags({ userId });
        }*/

        return ids.userId;
      } else {
        throw new Error('Failed to fetch OneSignal token on mobile');
      }
    } else {
      console.warn('Push notifications are disabled on mobile');
      return null;
    }
  } catch (error) {
    console.error('Error in fetchTokenForMobile:', error);
    throw error;
  }
};

// Mobile platform OneSignal initialization
const initializeOneSignalMobile = async () => {
  try {
    const { OneSignal } = window.Capacitor.Plugins;

    // Add debugging event listeners
    if (DEBUG) {
      OneSignal.addEventListener('notificationDisplay', (event) => {
        console.log('Mobile Notification displayed:', event);
      });
      OneSignal.addEventListener('notificationDismiss', (event) => {
        console.log('Mobile Notification dismissed:', event);
      });
    }

    OneSignal.startInit('46b06476-d1c3-4115-8b7e-a105cc5c480a')
      .inFocusDisplaying(OneSignal.OSInFocusDisplayOption.Notification)
      .endInit();

    const hasPermission = await OneSignal.promptForPushNotificationsWithUserResponse();

    if (hasPermission) {
      await fetchTokenWithRetry();
    } else {
      console.warn('Push notifications are disabled on mobile');
    }

    // Notification handlers
    OneSignal.addEventListener('notificationOpened', (jsonData) => {
      console.log('Notification opened (Mobile):', jsonData);
      // Add your notification opened handling logic here
    });

    OneSignal.addEventListener('notificationReceived', (jsonData) => {
      console.log('Notification received (Mobile):', jsonData);
      // Add your notification received handling logic here
    });

  } catch (error) {
    console.error('Error initializing OneSignal mobile:', error);
  }
};